import React, { useState } from 'react';
import './Form.scss';
import emailjs from '@emailjs/browser';
import { EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, EMAILJS_PUBLIC_KEY } from '../services/emailjs';
import { ArrowRight } from './Icons';

const services = [
  'Interior painting',
  'Exterior painting',
  'Kitchen cabinet painting',
  'Strata painting',
  'Commercial painting',
  'Warehouse / industrial painting',
  'Staining',
  'Not sure yet',
];

type Status = { kind: 'idle' } | { kind: 'sent' } | { kind: 'error'; message: string };

const initial = { name: '', phone: '', email: '', address: '', service: '', date: '', message: '' };

export function Form() {
  const [values, setValues] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const set = <K extends keyof typeof initial>(key: K, value: (typeof initial)[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setStatus({ kind: 'idle' });

    const templateParams = {
      to_name: 'Ivy League Painting',
      from_name: values.name,
      from_email: values.email,
      phone: values.phone,
      address: values.address,
      subject: values.service ? `Quote request: ${values.service}` : 'Quote request',
      message: values.message,
      booking: values.date ? 'Yes' : 'No',
      service: values.service,
      date: values.date || 'Not specified',
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);
      setStatus({ kind: 'sent' });
      setValues(initial);
    } catch (error) {
      console.error('There was a problem sending the email:', error);
      setStatus({ kind: 'error', message: 'Something went wrong. Please try again, or call us directly.' });
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <form className="form" onSubmit={handleSubmit}>
      <div className="form__row">
        <label className="field">
          <span>Full name</span>
          <input type="text" placeholder="Jane Smith" autoComplete="name" value={values.name} required onChange={(e) => set('name', e.target.value)} />
        </label>
        <label className="field">
          <span>Phone</span>
          <input type="tel" placeholder="(604) 000-0000" autoComplete="tel" value={values.phone} required onChange={(e) => set('phone', e.target.value)} />
        </label>
      </div>

      <div className="form__row">
        <label className="field">
          <span>Email</span>
          <input type="email" placeholder="you@example.com" autoComplete="email" value={values.email} required onChange={(e) => set('email', e.target.value)} />
        </label>
        <label className="field">
          <span>Property address</span>
          <input type="text" placeholder="Street, city" autoComplete="street-address" value={values.address} required onChange={(e) => set('address', e.target.value)} />
        </label>
      </div>

      <div className="form__row">
        <label className="field">
          <span>Service</span>
          <select value={values.service} onChange={(e) => set('service', e.target.value)} required>
            <option value="" disabled>
              Choose a service
            </option>
            {services.map((s) => (
              <option key={s} value={s.toLowerCase()}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>
            Preferred date <em>optional</em>
          </span>
          <input type="date" value={values.date} min={today} onChange={(e) => set('date', e.target.value)} />
        </label>
      </div>

      <label className="field">
        <span>Project details</span>
        <textarea rows={4} placeholder="Rooms, surfaces, colours you have in mind, timing…" value={values.message} required onChange={(e) => set('message', e.target.value)} />
      </label>

      <button type="submit" className="btn btn--blue btn--block form__submit" disabled={loading}>
        {loading ? 'Sending…' : 'Request my free quote'}
        <ArrowRight />
      </button>

      {status.kind !== 'idle' && (
        <div className="form__status" aria-live="polite">
          {status.kind === 'sent' && <p className="is-success">Thanks! Your request is on its way. We'll be in touch within one business day.</p>}
          {status.kind === 'error' && <p className="is-error">{status.message}</p>}
        </div>
      )}
    </form>
  );
}
